import React, { useState } from 'react';
import { Card, Form, Input, Button, message, Alert } from 'antd';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../services/api';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { validatePassword } from '../utils/validation';

const ForgotPasswordPage: React.FC = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [username, setUsername] = useState('');
    const [error, setError] = useState<string | null>(null);

    const handleSendOtp = async () => {
        try {
            setLoading(true);
            setError(null);
            const usernameValue = form.getFieldValue('username');
            
            const response = await authApi.generateOtp({ id: usernameValue });
            if (response.status === 'SUCCESS') {
                setOtpSent(true);
                setUsername(usernameValue);
                message.success('OTP sent successfully');
            } else {
                setError(response.message || 'Failed to send OTP');
            }
        } catch (error) {
            setError(error instanceof Error ? error.message : 'Failed to send OTP');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values: any) => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await authApi.forgotPassword({
                username: username,
                otp: values.otp,
                password: values.password
            });

            if (response.status === 'SUCCESS') {
                message.success('Password reset successful');
                navigate('/login');
            } else {
                setError(response.message || 'Failed to reset password');
            }
        } catch (error) {
            setError(error instanceof Error ? error.message : 'Failed to reset password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '100vh',
            background: '#f0f2f5'
        }}>
            <Card title="Forgot Password" style={{ width: 400 }}>
                {error && (
                    <Alert
                        message={error}
                        type="error"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}
                
                <Form
                    form={form}
                    onFinish={handleSubmit}
                    layout="vertical"
                >
                    {!otpSent && (
                        <Form.Item
                            name="username"
                            rules={[{ required: true, message: 'Please input your username!' }]}
                        >
                            <Input 
                                prefix={<UserOutlined />}
                                placeholder="Username"
                                disabled={loading}
                            />
                        </Form.Item>
                    )}

                    {otpSent && (
                        <>
                            <Form.Item
                                name="otp"
                                rules={[{ required: true, message: 'Please input the OTP!' }]}
                            >
                                <Input
                                    placeholder="Enter OTP"
                                    disabled={loading}
                                />
                            </Form.Item>

                            <Form.Item
                                name="password"
                                rules={[{ validator: (_, value) => validatePassword(_, value, true) }]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined />}
                                    placeholder="New Password"
                                    disabled={loading}
                                />
                            </Form.Item>

                            <Form.Item
                                name="confirmPassword"
                                dependencies={['password']}
                                rules={[
                                    { required: true, message: 'Please confirm your password!' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue('password') === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject('Passwords do not match!');
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined />}
                                    placeholder="Confirm Password"
                                    disabled={loading}
                                />
                            </Form.Item>
                        </>
                    )}

                    <Form.Item>
                        {!otpSent ? (
                            <Button
                                type="primary"
                                onClick={handleSendOtp}
                                loading={loading}
                                block
                            >
                                Send OTP
                            </Button>
                        ) : (
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                block
                            >
                                Reset Password
                            </Button>
                        )}
                    </Form.Item>

                    <Button 
                        type="link" 
                        onClick={() => navigate('/login')}
                        block
                    >
                        Back to Login
                    </Button>
                </Form>
            </Card>
        </div>
    );
};

export default ForgotPasswordPage; 